<template>
    <div class="mb-6">
        <div class="bg-white rounded shadow">
            <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                <h1 class="text-lg font-normal">
                    Create a property
                </h1>
            </header>

            <properties-store @success="onSuccess">
                <base-form
                    slot-scope="{
                        sid,
                        is_loading: is_saving,
                        data: property,
                        error,
                        store,
                        forget
                    }"
                    @submit="store(form)"
                >
                    <div class="px-4 py-3 border-b sm:px-6 md:py-6">
                        <validator-alert :sid="sid" />

                        <form-section>
                            <template slot="title">Address</template>
                            <template slot="description">
                                What's the address of the property?
                            </template>

                            <form-items-address
                                v-model="form.address"
                                :sid="sid"
                                :disabled="is_saving"
                            />
                        </form-section>
                    </div>

                    <div class="px-4 py-3 sm:px-6 md:py-6">
                        <form-section>
                            <template slot="title">Name</template>
                            <template slot="description">
                                Use your own naming convention. Optional.
                            </template>

                            <grid-layout>
                                <grid-layout-item class="md:w-1/2">
                                    <form-item
                                        :sid="sid"
                                        name="name"
                                    >
                                        <template slot="label">Name</template>

                                        <input-text
                                            v-model="form.name"
                                            :sid="sid"
                                            :placeholder="name"
                                            :disabled="is_saving"
                                            name="address[name]"
                                        />
                                    </form-item>
                                </grid-layout-item>
                            </grid-layout>
                        </form-section>
                    </div>

                    <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                        <base-button
                            :is-loading="is_saving"
                            type="submit"
                            class="w-full btn-blue md:w-auto"
                        >
                            Create Property
                        </base-button>
                    </div>

                    <error-modal
                        v-if="error && $error.is(error, { exclude: [422] })"
                        :error="error"
                    />

                    <template v-if="property">
                        <template v-if="$route.query.then === 'create-listing'">
                            <modal-success
                                :is-open="show_created_modal"
                            >
                                <template slot="heading">Property created!</template>

                                <p>
                                    You can now create a listing for this property.
                                </p>

                                <div
                                    slot="footer"
                                    class="flex flex-col md:flex-row-reverse"
                                >
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.listings.create'),
                                            query: {
                                                property: property.uuid
                                            }
                                        }"
                                        class="text-center btn btn-green"
                                    >
                                        Create a Listing
                                    </router-link>

                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.properties.show'),
                                            params: {
                                                property: property.uuid
                                            }
                                        }"
                                        type="button"
                                        class="mt-3 text-center btn md:mt-0 md:mr-3"
                                    >
                                        Go to Property
                                    </router-link>
                                </div>
                            </modal-success>
                        </template>

                        <template v-else-if="$route.query.then === 'create-tenancy'">
                            <modal-success
                                :is-open="show_created_modal"
                            >
                                <template slot="heading">Property created!</template>

                                <p>
                                    You can now create a tenancy for this property.
                                </p>

                                <div
                                    slot="footer"
                                    class="flex flex-col md:flex-row-reverse"
                                >
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.tenancies.create'),
                                            query: {
                                                property: property.uuid
                                            }
                                        }"
                                        class="text-center btn btn-green"
                                    >
                                        Create a Tenancy
                                    </router-link>

                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.properties.show'),
                                            params: {
                                                property: property.uuid
                                            }
                                        }"
                                        type="button"
                                        class="mt-3 text-center btn md:mt-0 md:mr-3"
                                    >
                                        Go to Property
                                    </router-link>
                                </div>
                            </modal-success>
                        </template>

                        <template v-else-if="$route.query.then === 'create-maintenance'">
                            <modal-success
                                :is-open="show_created_modal"
                            >
                                <template slot="heading">Property created!</template>

                                <p>
                                    You can now create a maintenance for this property.
                                </p>

                                <div
                                    slot="footer"
                                    class="flex flex-col md:flex-row-reverse"
                                >
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.maintenances.create'),
                                            query: {
                                                property: property.uuid
                                            }
                                        }"
                                        class="text-center btn btn-green"
                                    >
                                        Create a Maintenance
                                    </router-link>

                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.properties.show'),
                                            params: {
                                                property: property.uuid
                                            }
                                        }"
                                        type="button"
                                        class="mt-3 text-center btn md:mt-0 md:mr-3"
                                    >
                                        Go to Property
                                    </router-link>
                                </div>
                            </modal-success>
                        </template>

                        <template v-else>
                            <modal-success
                                :is-open="show_created_modal"
                                @close="(function () {
                                    if (!$route.query.intro) {
                                        show_created_modal = false;
                                        forget();
                                        reset();
                                    }
                                })()"
                            >
                                <template slot="heading">Property created!</template>

                                <p>
                                    Your property has been created and is ready for you to begin
                                    adding tenancies.
                                </p>

                                <div
                                    slot="footer"
                                    slot-scope="{ close }"
                                    class="flex flex-col md:flex-row-reverse"
                                >
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.properties.show'),
                                            params: {
                                                property: property.uuid
                                            },
                                            query: !!$route.query.intro
                                                ? { intro: '✓' }
                                                : {}
                                        }"
                                        class="text-center btn btn-green"
                                    >
                                        Go to Property
                                    </router-link>

                                    <button
                                        v-if="!$route.query.intro"
                                        type="button"
                                        @click.prevent="close"
                                        class="mt-3 text-center btn md:mt-0 md:mr-3"
                                    >
                                        Add another
                                    </button>
                                </div>
                            </modal-success>
                        </template>
                    </template>
                </base-form>
            </properties-store>
        </div>
    </div>
</template>

<script>
    import introJs from 'intro.js'
    import FormItemsAddress from '../../../../components/form-items-address'
    import PropertiesStore from '../../../../components/properties-store'

    const intro = introJs()

    export default {
        components: {
            FormItemsAddress,
            PropertiesStore
        },
        data () {
            return {
                show_created_modal: false,
                form: {
                    name: '',
                    address: {
                        street: '',
                        locality: '',
                        village: '',
                        town: '',
                        county: '',
                        postcode: ''
                    }
                }
            }
        },
        computed: {
            name () {
                if (!this.form.name && this.form.address.street) {
                    return this.form.address.street
                }

                return 'e.g. REN7024, Mum\'s, ...'
            }
        },
        beforeRouteLeave (to, from, next) {
            intro.exit()

            next()
        },
        created () {
            if (this.$route.query.intro) {
                this.introjs()
            }
        },
        methods: {
            reset () {
                this.$validator.reset(this.sid)

                Object.assign(this.$data, this.$options.data.apply(this))
            },
            onSuccess () {
                this.$track(
                    'dashboard.landlords.properties.create',
                    'form-submission',
                    'A landlord has created a property'
                )

                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            },
            introjs () {
                intro
                    .setOptions({
                        overlayOpacity: 100,
                        nextLabel: 'Next',
                        prevLabel: 'Prev',
                        doneLabel: 'Ok',
                        showBullets: false,
                        showProgress: false,
                        showStepNumbers: false,
                        disableInteraction: true,
                        steps: [
                            {
                                intro: "This guide will continue after you've created a property"
                            }
                        ]
                    })
                    .start()
            }
        }
    }
</script>
